import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Input } from 'react-toolbox';
import { Button } from 'react-toolbox/lib/button';
import { ThemeProvider } from 'react-css-themr';
import Card from 'components/Card';
import ScriptsIntro from 'scriptsIntro';
import JobReviewScript from 'jobReviewScript';
import InProgressCallScript from 'inProgressCallScript';
import FollowUpCallScript from 'followUpCallScript';
import FollowUpDeliveryCallScript from 'followUpDeliveryCallScript';
import WelcomeCallScript from 'welcomeCallScript';
import { DropdownDiv, ContainerDiv } from 'styledComponents';
import { urls } from 'helpers/url';
import { navigate } from 'actionCreators';
import { smoothScrollBy } from '../../../../../../../utils';
import {
  updatePhase,
  updatePhaseTask,
  addEventToPhase,
  overridePhaseStatus,
  removeFlagFromPhase,
} from 'actionCreators/phase';
import PhaseType from 'types/phase';
import { SCRIPT_STATUSES, RAILS_DATE } from 'config/constants';
import formStyles from 'styles/form';
import RTCard from 'styles/card';
import RTInput from 'styles/input';
import S from './styles.scss';

const theme = { RTInput, RTCard };

class PhaseCommunications extends React.Component {
  constructor(props) {
    super(props);
    this.handleBasicQuestion = this.handleBasicQuestion.bind(this);
    this.handleSpecificAnswered = this.handleSpecificAnswered.bind(this);
    this.handleCommNote = this.handleFormChange.bind(this, 'commNote');
    const { phase, phase: { phase_specific: { need_move_furniture } } } = this.props;
    this.state = {
      phase,
      originalFurnitureStatus: need_move_furniture,
      customerAnswered: '',
      canTalk: '',
      movingFurniture: '',
      jobNotFinished: '',
      deliveryOkay: '',
      customerUnsatisfied: '',
      commNote: '',
    };
  }

  handleScrollDown = percent => smoothScrollBy({ percent })

  handleBasicQuestion = (key, val) => this.setState({ [key]: val }, () =>
    this.handleScrollDown(18));

  handleSkipCanTalk = (key, val) => this.setState({ canTalk: val }, () =>
    this.handleBasicQuestion(key, val));

  handleSpecificAnswered = (answeredKey, specificKey, val) => {
    const { phase } = this.state;
    const answeredVal = val ? 'yes' : 'no';
    const newState = { [answeredKey]: answeredVal, phase };
    newState.phase.phase_specific[specificKey] = val;
    if (specificKey === 'need_move_furniture' && !val) {
      newState.phase.phase_specific.need_move_furniture_oversized = val;
    }
    this.setState(newState, () => this.handleScrollDown(18));
  }

  handleFormChange = (key, val) => this.setState({ [key]: val })

  updateTask = (id, slug) => {
    const { customerAnswered, canTalk, commNote,
            jobNotFinished, deliveryOkay, customerUnsatisfied } = this.state;
    return this.props.updatePhaseTask(id, slug, {
      task: { status: 'complete' },
      data: {
        answered: customerAnswered,
        can_talk: canTalk,
        comm_note: commNote,
        customer_unsatisfied: customerUnsatisfied || jobNotFinished || deliveryOkay === 'no',
      },
    });
  }

  addNote = (id) => {
    const { phase, commNote } = this.state;
    const noteProps = {
      name: 'Note',
      kind: 'note',
      data: { note: commNote },
    };
    this.props.addEventToPhase(id, noteProps)
      .then(() => this.navigateToHistory(phase)).catch(() => { });
  }

  navigateToHistory = phase => this.props.navigate(urls.phaseHistory(phase))

  finishCommunication = () => {
    const { phase, commNote } = this.state;
    if (this.isWelcomeCall()) {
      const { customerAnswered } = this.state;
      const flag = this.props.phase.flags.find(f => f.name === 'Welcome Call Needed');
      const removeFlag = () => (flag
        ? this.props.removeFlagFromPhase(phase.id, { id: flag.id, note: commNote })
        : Promise.resolve());
      // Only the voicemail (no-answer) branch sends the welcome text; on an
      // answered call the rep covers everything live.
      const sendWelcomeText = customerAnswered === 'no'
        ? this.props.updatePhase({ id: phase.id }, { communication: 'SendWelcomeTextMessage' })
        : Promise.resolve();
      sendWelcomeText
        .then(removeFlag)
        .then(() => this.navigateToHistory(phase))
        .catch(() => {});
      return;
    }
    const { jobNotFinished, deliveryOkay, customerUnsatisfied,
            phase: { id, phase_specific } } = this.state;
    const slug = this.determineSlug();
    if (['installation_customer_review', 'workorder_customer_review'].includes(slug)) {
      this.props.updatePhase({ id, phase_specific_attributes: phase_specific }, {})
        .then(() => this.updateTask(id, slug))
        .then(() => this.navigateToHistory(phase))
        .catch(() => {});
    } else if (jobNotFinished === 'yes' || deliveryOkay === 'no' ||
               (slug === 'follow_up_call' && this.state.phase.source === 'IFI' && customerUnsatisfied === 'yes')) this.addNote(id);
    else this.updateTask(id, slug).then(() => this.navigateToHistory(phase)).catch(() => {});
  }

  determineSlug = () => {
    const { phase: { status, type } } = this.props;
    switch (status) {
      case SCRIPT_STATUSES[0]:
        return type === 'Installation' ? 'installation_customer_review' : 'workorder_customer_review';
      case SCRIPT_STATUSES[1]:
        return 'install_in_progress_call';
      case SCRIPT_STATUSES[2]:
        return 'workorder_in_progress_call';
      case SCRIPT_STATUSES[3]:
        return 'follow_up_call';
      case SCRIPT_STATUSES[4]:
        return 'follow_up_delivery_call';
      default:
        return '';
    }
  }

  determineSubmitDisabled = () => {
    const { customerAnswered, canTalk, movingFurniture,
            jobNotFinished, deliveryOkay, customerUnsatisfied, commNote } = this.state;
    if (this.determineNoApptBlocked()) return true;
    if (!commNote) return true;
    if (customerAnswered === 'no') return false;
    const title = this.determineTitle();
    if (title === 'Job Review' && this.state.phase.source !== 'IFI') return canTalk === 'yes' ? !movingFurniture : !canTalk;
    if (title === 'Follow Up Call') {
      if (this.state.phase.source === 'IFI') return !customerUnsatisfied;
      return !jobNotFinished || (jobNotFinished === 'no' && !customerUnsatisfied);
    }
    if (title === 'Follow Up Delivery Call') return !deliveryOkay;
    return !customerAnswered;
  }

  isWelcomeCall = () =>
    this.props.phase.flags.some(f => f.name === 'Welcome Call Needed');

  determineTitle = () => {
    if (this.isWelcomeCall()) return 'Welcome Call';
    switch (this.state.phase.status) {
      case SCRIPT_STATUSES[0]:
        return 'Job Review';
      case SCRIPT_STATUSES[1]:
      case SCRIPT_STATUSES[2]:
        return 'In Progress Call';
      case SCRIPT_STATUSES[3]:
        return 'Follow Up Call';
      case SCRIPT_STATUSES[4]:
        return 'Follow Up Delivery Call';
      default:
        return '';
    }
  };

  determineNoApptBlocked = () => {
    const { phase: { appointments = [] } } = this.state;
    if (this.determineTitle() === 'Job Review') {
      return !appointments.length || !appointments.some(a => a.kind === 'installation' &&
        moment(a.start_date, RAILS_DATE).isAfter(moment()));
    }
    return false;
  }

  renderScriptBody = (title) => {
    const { phase: { status, source }, phase, customerAnswered, jobNotFinished, deliveryOkay,
            customerUnsatisfied, movingFurniture, originalFurnitureStatus } = this.state;
    if (this.isWelcomeCall()) {
      return (<WelcomeCallScript
        title={title}
        phase={phase}
        customerAnswered={customerAnswered}
      />);
    }
    switch (status) {
      case SCRIPT_STATUSES[0]:
        return (<JobReviewScript
          title={title}
          phase={phase}
          originalFurnitureStatus={originalFurnitureStatus}
          customerAnswered={customerAnswered}
          movingFurniture={movingFurniture}
          handleSpecificAnswered={this.handleSpecificAnswered}
        />);
      case SCRIPT_STATUSES[1]:
      case SCRIPT_STATUSES[2]:
        return (<InProgressCallScript
          title={title}
          customerAnswered={customerAnswered}
          source={source}
        />);
      case SCRIPT_STATUSES[3]:
        return (<FollowUpCallScript
          title={title}
          customerAnswered={customerAnswered}
          jobNotFinished={jobNotFinished}
          customerUnsatisfied={customerUnsatisfied}
          handleBasicQuestion={this.handleBasicQuestion}
          source={source}
        />);
      case SCRIPT_STATUSES[4]:
        return (<FollowUpDeliveryCallScript
          title={title}
          phase={phase}
          customerAnswered={customerAnswered}
          deliveryOkay={deliveryOkay}
          handleBasicQuestion={this.handleBasicQuestion}
        />);
      default:
        return null;
    }
  }

  render = () => {
    const { phase, customerAnswered, commNote } = this.state;
    const title = this.determineTitle();
    return (<Fragment>
      <ScriptsIntro
        user={this.props.user}
        phase={phase}
        title={title}
        customerAnswered={customerAnswered}
        handleBasicQuestion={title === 'Job Review' ?
          this.handleSkipCanTalk : this.handleBasicQuestion}
      />
      {customerAnswered && <ContainerDiv>
        <DropdownDiv condition={customerAnswered}>
          {this.renderScriptBody(title)}
        </DropdownDiv>
      </ContainerDiv>}
      <ThemeProvider theme={theme}>
        <Card className={S.card}>
          <div className={S.headerContainer}>
            <h4 className={S.title}>End Of Call</h4>
          </div>
          <div className={S.scriptContainer}>
            <Input
              label="Call Notes"
              multiline
              theme={formStyles}
              value={commNote || ''}
              className={formStyles.textArea}
              onChange={this.handleCommNote}
            />
            <br />
            <Button
              label="END COMMUNICATION"
              flat inverse ripple
              disabled={this.determineSubmitDisabled()}
              className={S.saveButton}
              onClick={() => this.finishCommunication()}
            />
          </div>
        </Card>
      </ThemeProvider>
    </Fragment>);
  }
}

PhaseCommunications.propTypes = {
  phase: PhaseType.isRequired,
  role: PropTypes.string.isRequired,
  user: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  updatePhase: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  addEventToPhase: PropTypes.func.isRequired,
  overridePhaseStatus: PropTypes.func.isRequired,
  removeFlagFromPhase: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    phase: state.phase.details,
    user: state.user.name,
    role: state.user.role,
  }), {
    navigate,
    updatePhase,
    updatePhaseTask,
    addEventToPhase,
    overridePhaseStatus,
    removeFlagFromPhase,
  },
)(PhaseCommunications);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseCommunications/index.jsx